import {
  createSlice,
  createAsyncThunk,
  createSelector } from
"@reduxjs/toolkit";
import { ApolloClient, gql, NormalizedCacheObject } from "@apollo/client";
import { RootState } from "@src/redux/store";
import * as TYPES from "@src/redux/types";

const GRAPHQL = gql`
  query getOrganization($filter: organizationFilterInput) {
    getOrganization(filter: $filter) {
      id
      name
      shortName
      usernamePrefix
      type
      groupId
      levelId
      parentOrganizationId
      email
      mobile
      logoUrl
      malePopulation
      femalePopulation
      thirdPopulation
      updateReferenceId
      childOrganizationList {
        id
        name
      }
      level {
        id
        name
        childLevelList {
          id
          name
          levelAttributeList {
            id
            name
            sortOrder
            isMandatory
            optionList
          }
        }
      }
      parentOrganization {
        shortName
        name
        id
      }
      rootLevelList {
        name
        id
      }
      deviceList {
        status
        brandId
        type
        macAddress
        referenceId
        id
      }
      kitList {
        effectiveTill
        effectiveFrom
        referenceId
        type
        id
      }
      error {
        statusCode
        message
      }
    }
  }
`;

const initialState: TYPES.OrganizationState = {
  result: null,
  error: null,
  loading: false
};

export const getOrganizationRequest = createAsyncThunk<
  TYPES.Organization,
  {filter?: TYPES.OrganizationFilterInput;},
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  "organization/get_organization",
  async (
  params: {filter?: TYPES.OrganizationFilterInput;},
  { extra: client }) =>
  {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params,
        fetchPolicy: "network-only"
      });
      return response.data.getOrganization;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || "An error occurred",
        statusCode: err.statusCode || "500"
      });
    }
  }
);

const getOrganizationSlice = createSlice({
  name: "get_organization",
  initialState: initialState,
  reducers: {
    getOrganizationClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getOrganizationRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(getOrganizationRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(getOrganizationRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: "Failed to fetch",
        statusCode: "500"
      };
      state.loading = false;
    });
  }
});

export const { getOrganizationClear } = getOrganizationSlice.actions;

export const getOrganization = createSelector(
  (state: RootState) => state.organization.getOrganization.result,
  (state: RootState) => state.organization.getOrganization.error,
  (state: RootState) => state.organization.getOrganization.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getOrganizationSlice.reducer;